<script>
import LeftPanelControls from '@comp/map/LeftPanelControls.vue'
import LeftPanelInstructions from '@comp/map/LeftPanelInstructions.vue'
import PlanningPopup from '@comp/PlanningPopup.vue'
import LeftPanelLineSection from '@comp/map/LeftPanelLineSection.vue'
import DeleteLineDialog from '@comp/map/DeleteLineDialog.vue'
import DeleteProjectDialog from '@comp/map/DeleteProjectDialog.vue'
import axios from 'axios'
import api from '@src/api.js'
import { useIndexStore } from '@src/store'
import { backUrl } from '@src/config.js'

export default {
  name: 'LeftPanel',
  components: {
    LeftPanelControls,
    LeftPanelInstructions,
    LeftPanelLineSection,
    PlanningPopup,
    DeleteLineDialog,
    DeleteProjectDialog,
  },
  props: {
    currentProject: {
      type: Object,
      default: () => null,
    },
    lines: {
      type: Array,
      required: true,
    },
  },
  emits: [
    'updateLineName',
    'deleteLine',
    'deleteProject',
    'editLine',
    'validateLines',
  ],
  data () {
    return {
      showLeftPanel: false,
      showPlanningPopup: false,
      showDeleteLineDialog: false,
      showDeleteProjectDialog: false,
      lineIndexToBeDeleted: -1,
      recordingData: {},
    }
  },
  computed: {
    noLine () {
      return !this.lines.length
    },
    improperRecordingDataDate () {
      return !this.recordingData.recording_end_date
    },
    isRecordable () {
      return (!this.noLine && !this.improperRecordingDataDate)
    },
    store () {
      return useIndexStore()
    },
    allLinesHaveName () {
      const lineNames = this.lines.map(line => line.name)
      return lineNames.every(el => el)
    },
    currentProjectNameEllipsed () {
      const name = this.currentProject.name
      return name.length > 30 ? name.substring(0, 30) + '…' : name
    },
  },
  watch: {
    currentProject: {
      async handler() {
        if (this.currentProject) {
          this.showLeftPanel = true
          await this.getRecordingParameters()
        }
      },
      deep: true,
    },
  },
  methods: {
    toggleShowLeftPanel () {
      this.showLeftPanel = !this.showLeftPanel
    },
    async openPlanningPopup () {
      await this.getRecordingParameters()
      this.showPlanningPopup = true
    },
    closePlanningPopup () {
      this.showPlanningPopup = false
    },
    openDeleteLineDialog (lineIndex) {
      this.lineIndexToBeDeleted = lineIndex
      this.showDeleteLineDialog = true
    },
    openDeleteProjectDialog () {
      this.showDeleteProjectDialog = true
    },
    closeDeleteLineDialog () {
      this.showDeleteLineDialog = false
    },
    closeDeleteProjectDialog () {
      this.showDeleteProjectDialog = false
    },
    async getRecordingParameters () {
      const projectId = this.currentProject.id
      try {
        const response = await axios.get(`${backUrl}projects/${projectId}/recording_date_range`)
        if (response.data.length) {
          this.recordingData = response.data[0]
        }
      } catch(error) {
        this.store.changeNotification({
          text: error.response ? error.response.data.error : error,
          autoClose: true,
          type: 'error',
        })
      }
    },
    async onDeleteProject () {
      try {
        await axios.delete(backUrl + 'projects/' + this.currentProject.id)
        this.closeDeleteProjectDialog()
        this.$emit('deleteProject')
        this.store.changeNotification({
          text: this.$gettext('Project successfully deleted'),
          autoClose: false,
          type: 'positive',
        })
      } catch(err) {
        if (err.status === 403) {
          this.store.changeNotification({
            text: this.$gettext('You are not authorized to delete this project'),
            autoClose: false,
            type: 'error',
          })
        } else {
          this.store.changeNotification({
            text: this.$gettext('An error occured during the deletion'),
            autoClose: false,
            type: 'error',
          })
        }
        console.error(err)
      }
    },
    async onDeleteLine () {
      this.closeDeleteLineDialog()
      const lines = [...this.lines]
      lines.splice(this.lineIndexToBeDeleted, 1)
      this.$emit('deleteLine', lines)
      this.lineIndexToBeDeleted = -1
      await this.onValidateLines(lines)
    },
    onEditLine (index) {
      this.$emit('editLine', index)
    },
    onUpdateLineName (lineName, index) {
      this.$emit('updateLineName', lineName, index)
    },
    async onValidateLines (listLines) {
      if (!listLines) {
        listLines = this.lines
      }
      return await axios.delete(backUrl + 'projects/' + this.currentProject.id + '/lines').then(() => {
        const promises = []
        for (const line of listLines) {
          const segments = []
          for (let j = 0; j < line.segments.length; j++) {
            const fromPoint = line.segments[j].from
            const toPoint = line.segments[j].to
            const splitFromPoint = fromPoint.split(',')
            const splittoPoint = toPoint.split(',')
            const segment = {
              order: j,
              origin: {
                type: 'Point',
                coordinates: [parseFloat(splitFromPoint[1]), parseFloat(splitFromPoint[0])],
              },
              destination: {
                type: 'Point',
                coordinates: [parseFloat(splittoPoint[1]), parseFloat(splittoPoint[0])],
              },
              average_duration: line.segments[j].average_duration,
              average_duration_text: line.segments[j].average_duration_text,
              distance: line.segments[j].distance,
              distance_text: line.segments[j].distance_text,
              start_address: line.segments[j].start_address,
              end_address: line.segments[j].end_address,
              polyline: line.segments[j].polyline.points,
            }
            segments.push(segment)
          }
          if (this.allLinesHaveName && this.currentProject.id) {
            promises.push(api.post(backUrl + 'projects/' + this.currentProject.id + '/lines', {
              id: line.id,
              name: line.name,
              segments: segments,
              draw_id: line.draw_id,
            }))
          }
        }
        return Promise.all(promises).then(() => {
          this.store.changeNotification({
            text: this.$gettext('Modifications successfully saved'),
            autoClose: false,
            type: 'positive',
          })
          this.$emit('validateLines')
        }).catch(err => {
          this.store.changeNotification({
            text: this.$gettext('An error occured during the creation'),
            autoClose: false,
            type: 'error',
          })
          console.log(err)
        })
      })
    },
  },
}
</script>
<template>
  <div
    v-if="currentProject"
    class="left-panel"
    :style="{'width': showLeftPanel ? '20%' : '0'}"
  >
    <div
      class="left-panel-toggle-btn"
      :class="showLeftPanel ? 'left-panel-toggle-btn-open' : 'left-panel-toggle-btn-close'"
      @click="toggleShowLeftPanel"
    >
      <v-icon
        size="small"
        color="secondary"
        :icon="showLeftPanel ? 'fas fa-chevron-left' : 'fas fa-chevron-right'"
      />
    </div>
    <Transition name="slide-fade">
      <div v-if="showLeftPanel">
        <div class="left-panel-title">
          {{ $gettext(`Project: ${currentProjectNameEllipsed}`) }}
          <v-icon
            size="small"
            color="$white"
            icon="fa-solid fa-trash"
            class="delete-project"
            @click="openDeleteProjectDialog"
          />
        </div>
        <v-col class="left-panel-content">
          <LeftPanelControls
            :current-project="currentProject"
            :is-recordable="isRecordable"
            @open-planning-dialog="openPlanningPopup"
          />
          <PlanningPopup
            :current-project="currentProject"
            :show="showPlanningPopup"
            :recording-data="recordingData"
            @close-popup="closePlanningPopup"
          />
          <v-divider />
          <LeftPanelInstructions />
          <v-divider />
          <LeftPanelLineSection
            :lines="lines"
            @validate-lines="onValidateLines"
            @update-line-name="onUpdateLineName"
            @edit-line="onEditLine"
            @delete-line="openDeleteLineDialog"
          />
        </v-col>
      </div>
    </Transition>
    <DeleteLineDialog
      :show="showDeleteLineDialog"
      @cancel-deletion="closeDeleteLineDialog"
      @delete-line="onDeleteLine"
    />
    <DeleteProjectDialog
      :show="showDeleteProjectDialog"
      @cancel-deletion="closeDeleteProjectDialog"
      @delete-project="onDeleteProject"
    />
  </div>
</template>
<style lang="scss" scoped>
.delete-project {
  padding-right: 1rem;
}
.delete-project:hover {
  transform: scale(1.15);
  cursor: pointer;
}
.left-panel {
  display: flex;
  max-height: calc(100vh - 50px);
  overflow: hidden auto;
  background-color: $white;
  transition: 0.3s;
  z-index: 20;
  box-shadow: 0 5px 10px rgba($black, 15%);
}
.left-panel-toggle-btn {
  position: absolute;
  left: 100%;
  width: 25px;
  z-index: 1;
  background-color: $white;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  transition: 0.3s;
  cursor: pointer;
  box-shadow: 6px 5px 10px rgba($black, 15%);
}
.left-panel-toggle-btn:hover {
  background-color: $secondary-light;
}
.left-panel-toggle-btn-open {
  left: calc(20% + 40px);
  background-color: $secondary;
}
.left-panel-toggle-btn-open .v-icon {
  color: $white !important;
}
.left-panel-toggle-btn:hover .v-icon {
  color: $secondary !important;
}
.left-panel-toggle-btn-close {
  left: 50px;
}
.left-panel-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 50px;
  line-height: 55px;
  padding-left: 20px;
  font-size: 1.1em;
  color: $white;
  background-color: $secondary;
  font-weight: 500;
}
.left-panel-content {
  display: flex;
  flex-direction: column;
  gap: 1ch;
  overflow-y: auto;
}
</style>
